import { Controller } from "@hotwired/stimulus"
import { roundPrice, grossFromNet, netFromGross } from "../../src/f_numbers"

// Logika specyficzna dla pozycji zamówienia (produkt):
// - przeliczanie kwot (netto/brutto/total) — wzorowane na fakturowni, używa f_numbers.js
// - dirty checking + checkbox "Zapisz/Aktualizuj produkt"
// Wyszukiwanie i dropdown obsługuje generyczny autocomplete controller.
export default class extends Controller {
  static targets = ["name", "amountNet", "amountGross", "tax", "code",
    "quantity", "totalNet", "totalGross", "productId", "discountPercent",
    "saveProduct", "saveProductWrapper", "updateProduct", "updateProductWrapper"]

  static values = {
    precision: { type: Number, default: 2 },
  }

  connect() {
    this._snapshot = {}
    this._originalProductId = this.hasProductIdTarget ? this.productIdTarget.value : ""
    this._hadProduct = this._originalProductId !== ""
    this._hideSaveProduct()
    this._hideUpdateProduct()
    this._saveSnapshot()

    // nasłuchuj na wybór z autocomplete
    this.element.addEventListener("autocomplete:select", this._onAutocompleteSelect)
  }

  disconnect() {
    this.element.removeEventListener("autocomplete:select", this._onAutocompleteSelect)
  }

  // Wywoływane przez autocomplete:select event
  _onAutocompleteSelect = (event) => {
    const product = event.detail
    if (!product) return

    if (this.hasProductIdTarget) this.productIdTarget.value = product.id
    this._originalProductId = product.id || ""
    this._hadProduct = true
    if (this.hasCodeTarget) this.codeTarget.value = product.code || ""
    if (this.hasAmountNetTarget) this.amountNetTarget.value = product.amount_net || ""
    if (this.hasAmountGrossTarget) this.amountGrossTarget.value = product.amount_gross || ""
    if (this.hasTaxTarget) this.taxTarget.value = product.tax || ""

    this._selecting = true
    this.recalculate()
    // sformatuj też pole netto (recalculate go nie nadpisuje)
    this._setVal(this.amountNetTarget, this._getVal(this.amountNetTarget), this.precisionValue)
    this._selecting = false
    this._saveSnapshot()
    this._hideSaveProduct()
    this._hideUpdateProduct()

    // przenieś focus na kolejne pole (kod)
    if (this.hasCodeTarget) this.codeTarget.focus()
  }

  onNameInput() {
    // nie czyścimy product_id — ID zostaje, chyba że user zaznaczy "Zapisz nowy produkt"
  }

  // wzajemne wykluczanie checkboxów
  uncheckUpdate() {
    if (this.hasUpdateProductTarget) this.updateProductTarget.checked = false
    if (this.hasProductIdTarget) this.productIdTarget.value = ""
  }

  uncheckCreate() {
    if (this.hasSaveProductTarget) this.saveProductTarget.checked = false
    if (this.hasProductIdTarget && this._originalProductId) this.productIdTarget.value = this._originalProductId
  }

  // --- Kalkulacje używające f_numbers.js (identyczna logika jak fakturownia) ---
  // Schemat: netto ↔ brutto przez tax, jednostkowa ↔ całkowita przez quantity

  // Zmiana ceny netto lub ilości lub VAT lub rabatu % → oblicz brutto, total netto, total brutto
  recalculate() {
    const p = this.precisionValue
    const qty = this._getQuantity()
    const net = this._getVal(this.amountNetTarget)
    const tax = this._parseTaxRate()
    const factor = this._discountFactor()

    const gross = grossFromNet(net, tax, 0, null, p)
    const totalNet = roundPrice(net * qty * factor, p)
    const totalGross = roundPrice(gross * qty * factor, p)

    this._setVal(this.amountGrossTarget, gross, p)
    this._setVal(this.totalNetTarget, totalNet, p)
    this._setVal(this.totalGrossTarget, totalGross, p)
    this._checkDirty()
  }

  // Zmiana ceny brutto → oblicz netto, total netto, total brutto
  recalculateFromGross() {
    const p = this.precisionValue
    const qty = this._getQuantity()
    const gross = this._getVal(this.amountGrossTarget)
    const tax = this._parseTaxRate()
    const factor = this._discountFactor()

    const net = netFromGross(gross, tax, 0, null, p)
    const totalNet = roundPrice(net * qty * factor, p)
    const totalGross = roundPrice(gross * qty * factor, p)

    this._setVal(this.amountNetTarget, net, p)
    this._setVal(this.totalNetTarget, totalNet, p)
    this._setVal(this.totalGrossTarget, totalGross, p)
    this._checkDirty()
  }

  // Zmiana total netto → oblicz cena netto, brutto, total brutto.
  // Total netto traktujemy jako wartość po rabacie (taką jaką widzi user w wierszu).
  recalculateFromTotalNet() {
    const p = this.precisionValue
    const qty = this._getQuantity()
    const totalNet = this._getVal(this.totalNetTarget)
    const tax = this._parseTaxRate()
    const factor = this._discountFactor()

    const baseNet = factor !== 0 ? totalNet / factor : totalNet
    const net = qty !== 0 ? roundPrice(baseNet / qty, p) : 0
    const gross = grossFromNet(net, tax, 0, null, p)
    const totalGross = grossFromNet(totalNet, tax, 0, null, p)

    this._setVal(this.amountNetTarget, net, p)
    this._setVal(this.amountGrossTarget, gross, p)
    this._setVal(this.totalGrossTarget, totalGross, p)
    this._checkDirty()
  }

  // Zmiana total brutto → oblicz total netto, cena netto, cena brutto.
  // Total brutto traktujemy jako wartość po rabacie.
  recalculateFromTotalGross() {
    const p = this.precisionValue
    const qty = this._getQuantity()
    const totalGross = this._getVal(this.totalGrossTarget)
    const tax = this._parseTaxRate()
    const factor = this._discountFactor()

    const totalNet = netFromGross(totalGross, tax, 0, null, p)
    const baseGross = factor !== 0 ? totalGross / factor : totalGross
    const baseNet = factor !== 0 ? totalNet / factor : totalNet
    const gross = qty !== 0 ? roundPrice(baseGross / qty, p) : 0
    const net = qty !== 0 ? roundPrice(baseNet / qty, p) : 0

    this._setVal(this.amountNetTarget, net, p)
    this._setVal(this.amountGrossTarget, gross, p)
    this._setVal(this.totalNetTarget, totalNet, p)
    this._checkDirty()
  }

  // Formatowanie pola na blur (trailing zeros + przecinek)
  formatField(event) {
    const p = this.precisionValue
    const val = this._getVal(event.target)
    this._setVal(event.target, roundPrice(val, p), p)
  }

  // --- Helpers ---

  _getQuantity() {
    const v = parseFloat(this.quantityTarget.value.replace(",", "."))
    return isNaN(v) ? 0 : v
  }

  _getVal(target) {
    const v = parseFloat(target.value.replace(",", "."))
    return isNaN(v) ? 0 : v
  }

  _parseTaxRate() {
    if (!this.hasTaxTarget) return 0
    const raw = this.taxTarget.value.trim().replace("%", "")
    const rate = parseFloat(raw)
    return isNaN(rate) ? 0 : rate
  }

  // Zwraca mnożnik rabatu (1 - percent/100). Brak rabatu = 1.
  _discountFactor() {
    if (!this.hasDiscountPercentTarget) return 1
    const raw = this.discountPercentTarget.value.trim().replace(",", ".")
    const pct = parseFloat(raw)
    if (isNaN(pct) || pct <= 0) return 1
    if (pct >= 100) return 0
    return 1 - (pct / 100)
  }

  _setVal(target, val, precision) {
    if (!target) return
    target.value = val.toFixed(precision).replace(".", ",")
  }

  // --- Snapshot & dirty checking ---

  checkDirty() {
    this._checkDirty()
  }

  _saveSnapshot() {
    this._snapshot = {
      name: this.hasNameTarget ? this.nameTarget.value : "",
      code: this.hasCodeTarget ? this.codeTarget.value : "",
      amount_net: this.hasAmountNetTarget ? this.amountNetTarget.value : "",
      amount_gross: this.hasAmountGrossTarget ? this.amountGrossTarget.value : "",
      tax: this.hasTaxTarget ? this.taxTarget.value : "",
    }
  }

  _checkDirty() {
    if (this._selecting) return
    const hasName = this.hasNameTarget && this.nameTarget.value.trim() !== ""

    if (this._hadProduct) {
      const dirty = this._isDirty()
      if (dirty) {
        const nameDirty = this._isNameDirty()
        // zmiana nazwy → oba checkboxy; zmiana tylko innych pól → tylko aktualizacja
        if (nameDirty) { this._showSaveProduct() } else { this._hideSaveProduct() }
        this._showUpdateProduct()
      } else {
        this._hideSaveProduct()
        this._hideUpdateProduct()
      }
    } else if (hasName) {
      this._showSaveProduct()
      this._hideUpdateProduct()
    } else {
      this._hideSaveProduct()
      this._hideUpdateProduct()
    }
  }

  _isDirty() {
    if (Object.keys(this._snapshot).length === 0) return false

    const current = {
      name: this.hasNameTarget ? this.nameTarget.value : "",
      code: this.hasCodeTarget ? this.codeTarget.value : "",
      amount_net: this.hasAmountNetTarget ? this.amountNetTarget.value : "",
      amount_gross: this.hasAmountGrossTarget ? this.amountGrossTarget.value : "",
      tax: this.hasTaxTarget ? this.taxTarget.value : "",
    }

    return Object.keys(this._snapshot).some(key => current[key] !== this._snapshot[key])
  }

  _isNameDirty() {
    if (!this.hasNameTarget || this._snapshot.name === undefined) return false
    return this.nameTarget.value !== this._snapshot.name
  }

  _showSaveProduct() {
    if (this.hasSaveProductWrapperTarget) this.saveProductWrapperTarget.classList.remove("hidden")
  }

  _hideSaveProduct() {
    if (this.hasSaveProductWrapperTarget) this.saveProductWrapperTarget.classList.add("hidden")
    if (this.hasSaveProductTarget) this.saveProductTarget.checked = false
  }

  _showUpdateProduct() {
    if (this.hasUpdateProductWrapperTarget) this.updateProductWrapperTarget.classList.remove("hidden")
  }

  _hideUpdateProduct() {
    if (this.hasUpdateProductWrapperTarget) this.updateProductWrapperTarget.classList.add("hidden")
    if (this.hasUpdateProductTarget) this.updateProductTarget.checked = false
  }
}
